"use client";

/**
 * One event in the History & Audit feed.
 *
 * Collapsed: relative time · actor + channel · field count.
 * Expanded:  per-field before/after (AuditFieldDiff) + a single
 *            `Restore this change` button scoped to THIS event — which is
 *            precisely what guarantee G1 makes an O(1) operation.
 *
 * The restore control is HIDDEN, not disabled, when `anyRevertable` is false:
 * offering a button that the route would reject is worse than not offering one.
 */

import React, { useCallback } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { AuditChannel } from "@wtc/audit/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { AuditEventDto } from "@/lib/audit/api-contract";

import { AuditFieldDiff } from "./AuditFieldDiff";

/** How the write reached us, spelled the way the feed reads it aloud. */
const CHANNEL_TEXT: Record<AuditChannel, string> = {
  ui: "Hub",
  meridian: "via Meridian",
  slack: "via Slack",
  mcp: "via MCP",
  cron: "cron",
  migration: "migration",
  unknown: "unknown source",
};

/** Channels that are the system acting on its own behalf, not a person. */
const SYSTEM_CHANNELS: ReadonlySet<AuditChannel> = new Set<AuditChannel>([
  "cron",
  "migration",
]);

/**
 * Actor line. 97% of backfilled events have no actor at all — that information
 * was never captured and cannot be reconstructed, so it says "Unknown" rather
 * than guessing.
 */
export function formatAuditActor(event: AuditEventDto): string {
  const { name, channel } = event.actor;
  const who = name ?? (SYSTEM_CHANNELS.has(channel) ? "system" : "Unknown");
  return `${who} · ${CHANNEL_TEXT[channel]}`;
}

const MS_PER_DAY = 86_400_000;

/**
 * Relative for recent history, absolute once "N months ago" stops being useful.
 * Pure and `now`-injected so it is deterministic under test.
 */
export function formatAuditEventTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "unknown time";

  const days = Math.floor((now.getTime() - then.getTime()) / MS_PER_DAY);
  if (days < 0) return "just now";
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 60) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? "1 wk ago" : `${weeks} wks ago`;
  }
  return then.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: now.getFullYear() === then.getFullYear() ? undefined : "numeric",
  });
}

/** Field count, or "anchor" for a pure snapshot event with nothing to diff. */
export function formatAuditFieldCount(event: AuditEventDto): string {
  const count = event.changes.length;
  if (count === 0) return "anchor";
  return count === 1 ? "1 field" : `${count} fields`;
}

const OP_TEXT = {
  INSERT: "Created",
  UPDATE: "Updated",
  DELETE: "Deleted",
} as const;

export interface AuditEventRowProps {
  event: AuditEventDto;
  expanded: boolean;
  onToggle: (eventId: string) => void;
  onRestore: (event: AuditEventDto) => void;
  /** A restore is already in flight — don't let a second one start. */
  restoreDisabled?: boolean;
  /** Injected in tests so relative times are deterministic. */
  now?: Date;
}

export function AuditEventRow({
  event,
  expanded,
  onToggle,
  onRestore,
  restoreDisabled = false,
  now,
}: AuditEventRowProps) {
  const handleToggle = useCallback(() => onToggle(event.eventId), [onToggle, event.eventId]);
  const handleRestore = useCallback(() => onRestore(event), [onRestore, event]);

  const isRevert = event.source === "revert";
  const blockedFieldCount = event.changes.filter((change) => !change.revertable).length;

  return (
    <li className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {expanded ? (
          <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        )}

        <span className="w-24 shrink-0 text-sm text-muted-foreground">
          <time dateTime={event.at} title={new Date(event.at).toLocaleString()}>
            {formatAuditEventTime(event.at, now)}
          </time>
        </span>

        <span className="min-w-0 flex-1 truncate text-sm">
          {event.op !== "UPDATE" && (
            <span className="mr-2 font-medium">{OP_TEXT[event.op]}</span>
          )}
          {formatAuditActor(event)}
          {isRevert && (
            <Badge variant="outline" className="ml-2 text-[10px] uppercase tracking-wide">
              Restore
            </Badge>
          )}
        </span>

        <span className="shrink-0 text-xs text-muted-foreground">
          {formatAuditFieldCount(event)}
        </span>
      </button>

      {expanded && (
        <div className="space-y-2 px-3 pb-3 pl-10">
          {event.changes.length === 0 ? (
            // A deletion records no per-field diff — the whole row went — so the
            // generic "snapshot anchor" line would describe the mechanism and
            // hide the consequence. Restoring here is an UNDELETE, and the one
            // thing the reader must know before clicking is that the record's
            // cascade-deleted children are not part of it.
            event.op === "DELETE" ? (
              <p className="text-sm text-muted-foreground">
                The whole record was deleted. Restoring recreates it at its original id;
                records deleted along with it — its tickets, tasks and communications — do
                not come back.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                No field changes recorded — this event is a snapshot anchor.
              </p>
            )
          ) : (
            event.changes.map((change) => (
              <AuditFieldDiff key={change.field} change={change} />
            ))
          )}

          {/* Hidden entirely when nothing here can be written back. */}
          {event.anyRevertable && (
            <div className={cn("flex flex-wrap items-center justify-end gap-2 pt-1")}>
              {blockedFieldCount > 0 && (
                <span className="text-xs text-muted-foreground">
                  {blockedFieldCount === 1
                    ? "1 field cannot be restored — see above."
                    : `${blockedFieldCount} fields cannot be restored — see above.`}
                </span>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRestore}
                disabled={restoreDisabled}
              >
                {event.op === "DELETE" ? "Restore this record" : "Restore this change"}
              </Button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
